// Typed client for the authoritative economy endpoints (gacha odds + pity live
// ONLY on the backend, per CLAUDE.md). Dev requests go through the Vite proxy
// ('/api' → :8078). Not wired into UI yet — the GachaScene comes next.

import { t } from '../i18n'
import type { BackendAction } from './JKGame_api'

export type Rarity = 'SSR' | 'SR' | 'R'

export interface GachaItem {
  id: string
  name: string
  rarity: Rarity
  kind: string
}

export interface DailyMission {
  id: string
  goal: number
  metric: string
  reward: Record<string, number>
}

export interface GemPack {
  id: string
  gem: number // base grant
  bonus: number // extra value on bigger packs (total = gem + bonus)
  usd_cents: number // real Stripe charge amount (US-dollar minor units)
}

export interface EconomyConfig {
  currencies: { soft: string; hard: string }
  gacha: { cost_gem: number; pity_ssr: number; rates: Record<string, number>; pool: GachaItem[] }
  season: { max_level: number; xp_to_next_lv1: number }
  daily_missions: DailyMission[]
  shop: { currency: string; gem_packs: GemPack[] }
}

export interface GachaResult {
  results: GachaItem[]
  since_ssr: number
  gem_cost: number
}

const BASE = '/api'

export interface SeasonWindow {
  id: string
  start: string
  end: string
  days_total: number
  days_left: number
  active: boolean
}

/** Live season window + countdown (account-less; uses the server's date). */
export async function apiSeason(): Promise<SeasonWindow> {
  const r = await fetch(`${BASE}/season`)
  if (!r.ok) throw new Error(`GET /season → ${r.status}`)
  return r.json() as Promise<SeasonWindow>
}

export type RankTier = 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond' | 'master'
export interface LeaderboardRow {
  rank: number
  handle: string
  points: number
  wins: number
  best_rank: number
  matches: number
  tier?: RankTier // ranked 段位 (derived from season points)
}
export interface DailyChallenge {
  date: string
  seed: number
}

/** Local mirror of the backend economy.daily_seed (FNV-1a 32-bit over the ISO date). Lets offline
 *  ranked use the same per-day seed the server would, so a day's challenge is consistent. */
export function dailySeed(dateStr: string): number {
  let h = 2166136261 >>> 0
  for (let i = 0; i < dateStr.length; i++) {
    h = (h ^ dateStr.charCodeAt(i)) >>> 0
    h = Math.imul(h, 16777619) >>> 0
  }
  return h || 1
}

export function todayISO(): string {
  return new Date().toISOString().slice(0, 10)
}

/** Today's challenge seed (server-authoritative). Ranked uses it so all players face the identical
 *  island + bots → a fair pure-skill board. Falls back to the local mirror when offline. */
export async function apiDaily(): Promise<DailyChallenge> {
  const r = await fetch(`${BASE}/daily`)
  if (!r.ok) throw new Error(`GET /daily → ${r.status}`)
  return r.json() as Promise<DailyChallenge>
}

export interface GhostRunData {
  handle: string
  char: string | null
  actions: { type: string; zone?: number | null; target_id?: string | null; kind?: string | null; boon_id?: string | null }[]
  rank: number
  kills: number
  steps: number
}

/** Async-PvP ghosts recorded on a seed (best rank first), for in-match replay. `handle` excludes
 *  your own ghost so you race OTHERS. Each ghost carries its char + action sequence to replay. */
export async function apiGhosts(seed: number, handle?: string, limit = 5): Promise<{ seed: number; ghosts: GhostRunData[] }> {
  const q = new URLSearchParams({ seed: String(seed), limit: String(limit) })
  if (handle) q.set('handle', handle)
  const r = await fetch(`${BASE}/ghosts?${q.toString()}`)
  if (!r.ok) throw new Error(`GET /ghosts → ${r.status}`)
  return r.json() as Promise<{ seed: number; ghosts: GhostRunData[] }>
}

export type LeaderboardMode = 'casual' | 'ranked'
export interface LeaderboardResult {
  season_id: string
  mode: LeaderboardMode
  entries: LeaderboardRow[]
  me: LeaderboardRow | null
}

/** Season leaderboard for a `mode` (casual / ranked): top-N by season points + (when a handle is
 *  given) that player's own standing. Account-less read (public ranking data). */
export async function apiLeaderboard(handle?: string, limit = 10, mode: LeaderboardMode = 'casual'): Promise<LeaderboardResult> {
  const q = new URLSearchParams({ limit: String(limit), mode })
  if (handle) q.set('handle', handle)
  const r = await fetch(`${BASE}/leaderboard?${q.toString()}`)
  if (!r.ok) throw new Error(`GET /leaderboard → ${r.status}`)
  return r.json() as Promise<LeaderboardResult>
}

export async function apiEconomyConfig(): Promise<EconomyConfig> {
  const r = await fetch(`${BASE}/economy/config`)
  if (!r.ok) throw new Error(`GET /economy/config → ${r.status}`)
  return r.json() as Promise<EconomyConfig>
}

export async function apiGachaPreview(seed: number, pulls = 10, sinceSsr = 0): Promise<GachaResult> {
  const r = await fetch(`${BASE}/economy/gacha/preview`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ seed, pulls, since_ssr: sinceSsr }),
  })
  if (!r.ok) throw new Error(`POST /economy/gacha/preview → ${r.status}`)
  return r.json() as Promise<GachaResult>
}

export interface WalletState {
  coin: number
  gem: number
  souls: number // roguelite meta currency (roster unlocks) — backend-authoritative
  xp: number
  level: number
  since_ssr: number
  owned: Record<string, number>
  unlocked?: string[] // roster character ids unlocked with souls (backend mode)
}

export interface UnlockCharResult {
  ok: boolean
  reason?: string // cantAfford | already | notSoulPath
  wallet?: WalletState
}

/** Authoritative roster unlock: spend backend souls to unlock a character. Server enforces the
 *  cost + ownership (souls can't be forged). Returns the updated wallet on success. */
export async function apiUnlockChar(handle: string, charId: string): Promise<UnlockCharResult> {
  const r = await fetch(`${BASE}/account/${encodeURIComponent(handle)}/unlock_char`, {
    method: 'POST',
    headers: accountHeaders(true),
    body: JSON.stringify({ char_id: charId }),
  })
  if (!r.ok) throw new Error(`POST /account/${handle}/unlock_char → ${r.status}`)
  return r.json() as Promise<UnlockCharResult>
}

export interface WalletPullResult {
  ok: boolean
  reason?: string
  results?: GachaItem[]
  wallet: WalletState
  gem_cost: number
}

export async function apiWalletNew(): Promise<{ id: string; wallet: WalletState }> {
  const r = await fetch(`${BASE}/wallet/new`, { method: 'POST' })
  if (!r.ok) throw new Error(`POST /wallet/new → ${r.status}`)
  return r.json() as Promise<{ id: string; wallet: WalletState }>
}

export async function apiWalletGet(id: string): Promise<WalletState> {
  const r = await fetch(`${BASE}/wallet/${id}`)
  if (!r.ok) throw new Error(`GET /wallet/${id} → ${r.status}`)
  return r.json() as Promise<WalletState>
}

export async function apiWalletPull(id: string, seed: number, pulls = 10): Promise<WalletPullResult> {
  const r = await fetch(`${BASE}/wallet/${id}/pull`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ seed, pulls }),
  })
  if (!r.ok) throw new Error(`POST /wallet/${id}/pull → ${r.status}`)
  return r.json() as Promise<WalletPullResult>
}

/** Stable per-device account handle (for persistent server wallets). For a registered account
 *  this is the normalized (lowercased) handle returned by login/register. */
export function getHandle(): string {
  try {
    const key = 'jkgame.handle'
    let h = localStorage.getItem(key)
    if (!h) {
      h = `guest-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`
      localStorage.setItem(key, h)
    }
    return h
  } catch {
    return 'guest'
  }
}

const TOKEN_KEY = 'jkgame.token'

/** The current session token (empty when not logged in / guest). */
export function getToken(): string {
  try {
    return localStorage.getItem(TOKEN_KEY) || ''
  } catch {
    return ''
  }
}

export function setToken(token: string): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token)
    else localStorage.removeItem(TOKEN_KEY)
  } catch {
    /* ignore */
  }
}

/** Headers for account/economy calls: the session token (so the server can authenticate a
 *  registered account) plus an optional JSON content type. */
function accountHeaders(json = false): Record<string, string> {
  const h: Record<string, string> = {}
  const tok = getToken()
  if (tok) h['X-JK-Token'] = tok
  if (json) h['Content-Type'] = 'application/json'
  return h
}

export interface AuthResult {
  ok: boolean
  token?: string
  handle?: string // the normalized handle the server stored
  reason?: string
}

/** Register a new account (handle + password). Returns a session token on success. */
export async function apiRegister(handle: string, password: string, displayName = ''): Promise<AuthResult> {
  const r = await fetch(`${BASE}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ handle, password, display_name: displayName }),
  })
  if (!r.ok) throw new Error(`POST /auth/register → ${r.status}`)
  return r.json() as Promise<AuthResult>
}

/** Log in to an existing account. Returns a fresh session token on success. */
export async function apiLogin(handle: string, password: string): Promise<AuthResult> {
  const r = await fetch(`${BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ handle, password }),
  })
  if (!r.ok) throw new Error(`POST /auth/login → ${r.status}`)
  return r.json() as Promise<AuthResult>
}

export async function apiAccountWallet(handle: string): Promise<WalletState> {
  const r = await fetch(`${BASE}/account/${encodeURIComponent(handle)}/wallet`, { method: 'POST', headers: accountHeaders() })
  if (!r.ok) throw new Error(`POST /account/${handle}/wallet → ${r.status}`)
  return r.json() as Promise<WalletState>
}

export interface CheckoutResult {
  ok: boolean
  url?: string // Stripe hosted-Checkout URL to redirect to
  reason?: string
}

/** Create a Stripe Checkout Session for a gem pack; the caller redirects to `url`. Gems are
 *  granted server-side only after Stripe confirms payment (the webhook), never by the client. */
export async function apiCreateCheckout(handle: string, packId: string): Promise<CheckoutResult> {
  const r = await fetch(`${BASE}/account/${encodeURIComponent(handle)}/checkout`, {
    method: 'POST',
    headers: accountHeaders(true),
    body: JSON.stringify({ pack_id: packId }),
  })
  if (!r.ok) throw new Error(`POST /account/${handle}/checkout → ${r.status}`)
  return r.json() as Promise<CheckoutResult>
}

export async function apiAccountPull(handle: string, seed: number, pulls = 10): Promise<WalletPullResult> {
  const r = await fetch(`${BASE}/account/${encodeURIComponent(handle)}/pull`, {
    method: 'POST',
    headers: accountHeaders(true),
    body: JSON.stringify({ seed, pulls }),
  })
  if (!r.ok) throw new Error(`POST /account/${handle}/pull → ${r.status}`)
  return r.json() as Promise<WalletPullResult>
}

export interface SettleResult {
  wallet: WalletState
  match: { xp: number; coin: number; placement: { xp: number; coin: number } }
  missions: Array<{ id: string; title?: string; reward: Record<string, number> }>
  mission_total: { xp: number; coin: number }
  gained: { xp: number; coin: number; souls?: number }
  souls?: number // souls earned this match (replay/clamped settlement)
  level_before: number
  level_after: number
  season: { level: number; into: number; next: number }
}

export interface MatchOutcome {
  rank: number
  kills: number
  steps: number
  total: number
}

/** Authoritative end-of-match settlement (coin + season XP + daily missions). */
export async function apiAccountSettle(handle: string, outcome: MatchOutcome): Promise<SettleResult> {
  const r = await fetch(`${BASE}/account/${encodeURIComponent(handle)}/settle`, {
    method: 'POST',
    headers: accountHeaders(true),
    body: JSON.stringify(outcome),
  })
  if (!r.ok) throw new Error(`POST /account/${handle}/settle → ${r.status}`)
  return r.json() as Promise<SettleResult>
}

export interface ReplaySettleIn {
  seed: number
  bots: number
  char: string | null
  actions: BackendAction[]
  ranked?: boolean // fair mode: server replays without loadout/boon + records to the ranked board
}

/** A-方案 settlement: submit the match seed + char + action sequence; the server replays it and
 *  settles from the TRUE outcome (a forged result is impossible). Preferred over apiAccountSettle. */
export async function apiSettleReplay(handle: string, body: ReplaySettleIn): Promise<SettleResult> {
  const r = await fetch(`${BASE}/account/${encodeURIComponent(handle)}/settle_replay`, {
    method: 'POST',
    headers: accountHeaders(true),
    body: JSON.stringify(body),
  })
  if (!r.ok) throw new Error(`POST /account/${handle}/settle_replay → ${r.status}`)
  return r.json() as Promise<SettleResult>
}

export interface MissionProgress {
  id: string
  title?: string
  goal: number
  metric: string
  reward: Record<string, number>
  current: number
  done: boolean
}

const KNOWN_MISSION_IDS = new Set(['play_1', 'kill_3', 'survive_20'])

/** Localized mission name that never shows a raw key/id: prefer a server-set
 *  title, else a known built-in i18n key, else a generic localized label. */
export function missionLabel(id: string, title?: string): string {
  if (title && title.trim()) return title
  if (KNOWN_MISSION_IDS.has(id)) return t(`mission.${id}` as Parameters<typeof t>[0])
  return t('mission.generic')
}

export interface AccountProgress {
  day: string
  season: { level: number; into: number; next: number }
  missions: MissionProgress[]
}

/** Read-only season level + daily-mission progress for an account. */
export async function apiAccountProgress(handle: string): Promise<AccountProgress> {
  const r = await fetch(`${BASE}/account/${encodeURIComponent(handle)}/progress`, { headers: accountHeaders() })
  if (!r.ok) throw new Error(`GET /account/${handle}/progress → ${r.status}`)
  return r.json() as Promise<AccountProgress>
}

export interface BattlePassReward {
  coin?: number
  item?: { id: string; kind: string }
}

export interface BattlePassEntry {
  level: number
  reward: BattlePassReward
}

export interface BattlePassState {
  season: { level: number; into: number; next: number }
  claimable: BattlePassEntry[]
  claimed: number[]
}

export interface BattlePassClaim {
  wallet: WalletState
  claimed: BattlePassEntry[]
  gained: { coin: number }
}

/** Read-only BattlePass free-track snapshot. */
export async function apiAccountBattlepass(handle: string): Promise<BattlePassState> {
  const r = await fetch(`${BASE}/account/${encodeURIComponent(handle)}/battlepass`, { headers: accountHeaders() })
  if (!r.ok) throw new Error(`GET /account/${handle}/battlepass → ${r.status}`)
  return r.json() as Promise<BattlePassState>
}

export interface BattlePassTrackEntry {
  level: number
  reward: BattlePassReward
  claimed: boolean
}

export interface BattlePassTrack {
  season: { level: number; into: number; next: number }
  track: BattlePassTrackEntry[]
  claimed_count: number
}

/** Read-only full BattlePass free-track (every level + claimed flag). */
export async function apiBattlepassTrack(handle: string): Promise<BattlePassTrack> {
  const r = await fetch(`${BASE}/account/${encodeURIComponent(handle)}/battlepass/track`, { headers: accountHeaders() })
  if (!r.ok) throw new Error(`GET /account/${handle}/battlepass/track → ${r.status}`)
  return r.json() as Promise<BattlePassTrack>
}

/** Claim all reached-but-unclaimed free-track rewards. */
export async function apiClaimBattlepass(handle: string): Promise<BattlePassClaim> {
  const r = await fetch(`${BASE}/account/${encodeURIComponent(handle)}/battlepass/claim`, { method: 'POST', headers: accountHeaders() })
  if (!r.ok) throw new Error(`POST /account/${handle}/battlepass/claim → ${r.status}`)
  return r.json() as Promise<BattlePassClaim>
}

/** Localized rarity label (SSR / SR / R). */
export function rarityLabel(r: Rarity): string {
  return r === 'SSR' ? t('gacha.ssr') : r === 'SR' ? t('gacha.sr') : t('gacha.r')
}

export const RARITY_COLOR: Record<Rarity, string> = {
  SSR: '#ffce4d',
  SR: '#b483ff',
  R: '#9aa4b2',
}
